import { useState, useEffect, useCallback, useRef } from 'react';
import { inventoryApi } from '../services/inventoryApi';
import type {
    InventoryItem,
    InventoryFilterOptions,
    InventoryFilters,
    InventoryStats,
    InventoryFlagStats,
    InventorySortField,
    SortOrder,
} from '../types/inventory';

interface UseInventoryReturn {
    items: InventoryItem[];
    total: number;
    loading: boolean;
    error: string | null;
    stats: InventoryStats | null;
    flagStats: InventoryFlagStats | null;
    filters: InventoryFilters;
    filterOptions: InventoryFilterOptions | null;
    sortBy: InventorySortField;
    sortOrder: SortOrder;
    page: number;
    limit: number;
    setFilters: (filters: InventoryFilters) => void;
    setSort: (field: InventorySortField) => void;
    setPage: (page: number) => void;
    refresh: () => void;
}

const SESSION_STORAGE_KEY = 'slowmover-inventory-state';

interface PersistedState {
    filters: InventoryFilters;
    sortBy: InventorySortField;
    sortOrder: SortOrder;
}

const DEFAULT_FILTERS: InventoryFilters = { classGroup: 'Piombi' };
const DEFAULT_SORT_BY: InventorySortField = 'stockValueEUR';
const DEFAULT_SORT_ORDER: SortOrder = 'DESC';

function loadPersistedState(): PersistedState {
    if (typeof window === 'undefined') {
        return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
    }
    try {
        const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!raw) return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
        const parsed = JSON.parse(raw) as Partial<PersistedState>;
        // A bare warehouse code is from before the "<company>|<code>" key and matches no option
        const filters = parsed.filters && !parsed.filters.warehouseCode?.includes('|')
            ? { ...parsed.filters, warehouseCode: undefined }
            : parsed.filters;
        return {
            filters: filters ?? DEFAULT_FILTERS,
            sortBy: parsed.sortBy ?? DEFAULT_SORT_BY,
            sortOrder: parsed.sortOrder ?? DEFAULT_SORT_ORDER,
        };
    } catch {
        return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
    }
}

export function useInventory(): UseInventoryReturn {
    const [items, setItems] = useState<InventoryItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<InventoryStats | null>(null);
    const [flagStats, setFlagStats] = useState<InventoryFlagStats | null>(null);

    const initial = loadPersistedState();
    const [filters, setFiltersState] = useState<InventoryFilters>(initial.filters);
    const [filterOptions, setFilterOptions] = useState<InventoryFilterOptions | null>(null);
    const [sortBy, setSortBy] = useState<InventorySortField>(initial.sortBy);
    const [sortOrder, setSortOrder] = useState<SortOrder>(initial.sortOrder);
    const [page, setPage] = useState(1);
    const limit = 50;
    const refreshCounter = useRef(0);

    // Persist filter + sort state to sessionStorage (per-tab)
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const payload: PersistedState = { filters, sortBy, sortOrder };
            window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // Ignore storage errors (quota, private mode, etc.)
        }
    }, [filters, sortBy, sortOrder]);

    // Re-fetch filter options when relevant filters change
    // A slower, earlier answer must not overwrite a later one: the unscoped options of all
    // companies arriving last would offer warehouses the list no longer contains.
    useEffect(() => {
        let cancelled = false;
        inventoryApi.getFilters(filters)
            .then(options => { if (!cancelled) setFilterOptions(options); })
            .catch(err => console.error('Failed to load filter options:', err));
        return () => { cancelled = true; };
    }, [filters.companyCode, filters.inventoryClass, filters.classGroup,
        filters.businessUnit, filters.parentCategory,
        filters.warehouseCountry, filters.supplierCode]);

    // Load data + stats when filters/sort/page change
    useEffect(() => {
        let cancelled = false;
        const currentRefresh = ++refreshCounter.current;

        setLoading(true);
        setError(null);

        Promise.all([
            inventoryApi.list({ page, limit, sortBy, sortOrder, filters }),
            inventoryApi.getStats(filters),
            inventoryApi.getFlagStats(filters),
        ])
            .then(([listResult, statsResult, flagStatsResult]) => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setItems(listResult.items);
                    setTotal(listResult.total);
                    setStats(statsResult);
                    setFlagStats(flagStatsResult);
                }
            })
            .catch(err => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setError(err.message);
                }
            })
            .finally(() => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [filters, sortBy, sortOrder, page]);

    const setFilters = useCallback((newFilters: InventoryFilters) => {
        setFiltersState(newFilters);
        setPage(1);
    }, []);

    const setSort = useCallback((field: InventorySortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
            } else {
                setSortOrder('DESC');
            }
            return field;
        });
        setPage(1);
    }, []);

    const refresh = useCallback(() => {
        refreshCounter.current++;
        setLoading(true);
        setError(null);

        Promise.all([
            inventoryApi.list({ page, limit, sortBy, sortOrder, filters }),
            inventoryApi.getStats(filters),
            inventoryApi.getFlagStats(filters),
        ])
            .then(([listResult, statsResult, flagStatsResult]) => {
                setItems(listResult.items);
                setTotal(listResult.total);
                setStats(statsResult);
                setFlagStats(flagStatsResult);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [page, sortBy, sortOrder, filters]);

    return {
        items, total, loading, error, stats, flagStats,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    };
}
